<section class="go-container">
  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy">The Action Sheet component should be used when a list of actions or links need to be grouped and hidden behind a button.</p>
  
        <p class="go-body-copy">
          The <code class="code-block--inline">go-action-sheet</code> element must be accompanied by
          <code class="code-block--inline">go-panel</code> children.
          See <a [routerLink]="['/ui-kit/action-sheet/panel']">Panel</a> for details.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="bindings"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">placement</h4>
        <p class="go-body-copy">
          Optional binding that defines the placement of action sheet to render. Current supported positions:
          <code class="code-block--inline">bottom</code>, <code class="code-block--inline">right</code>.
          By default, action sheet will render with <code class="code-block--inline">bottom</code> set.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h4 class="go-heading-4">shiftLeft</h4>
        <p class="go-body-copy">
          If true, <code class="code-block--inline">shiftLeft</code>
          will move the pop up portion of the action sheet to the left and adjust the arrow to the right.
          This is useful for items close to the right edge of the page or container.
        </p>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="action-sheet-structure">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Action Sheet Structure</h2>
      <go-copy cardId="action-sheet-structure" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <p class="go-body-copy">
        The action sheet component is split up into two sections. The trigger and the content.
        The simplest way to get the action sheet working is with two <code class="code-block--inline">ng-container</code>s.
      </p>

      <code [highlight]="structureHtml"></code>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="simple">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Simple</h2>
      <go-copy cardId="simple" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column--100 go-column go-column--no-padding">
          <p class="go-body-copy">
            A simple example to show the basic functionality of an action sheet.
          </p>
        </div>
        <div class="go-column go-column--50 go-column--no-padding">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="simpleHtml"></code>
        </div>
        <div class="go-column go-column--50 go-column--no-padding go-column--collapse">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-action-sheet>
            <ng-container go-action-sheet__button>
              <go-button>
                Action Sheet
              </go-button>
            </ng-container>
            <ng-container go-action-sheet-content>
              <go-panel panelContent="Panel 1">
              </go-panel>
              <go-panel panelContent="Panel 2">
              </go-panel>
              <go-panel panelContent="Panel 3">
              </go-panel>
            </ng-container>
          </go-action-sheet>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="with-an-accordion" #accordion>
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">With an Accordion</h2>
      <go-copy cardId="with-an-accordion" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column--100 go-column go-column--no-padding">
          <p class="go-body-copy">
            This example shows an instance where an accordion is being used inside of an action sheet.
            Passing <code class="code-block--inline">true</code> to the accordion's
            <code class="code-block--inline">forActionSheet</code> input is necessary for the accordion's styles
            to match the rest of the action sheet.
          </p>
        </div>
        <div class="go-column go-column--50 go-column--no-padding">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <code [highlight]="accordionHtml"></code>
        </div>
        <div class="go-column go-column--50 go-column--no-padding go-column--collapse">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
          <go-action-sheet>
            <ng-container go-action-sheet__button>
              <go-button>
                Action Sheet
              </go-button>
            </ng-container>
            <ng-container go-action-sheet-content>
              <go-panel panelContent="Home" icon="home">
              </go-panel>
              <go-accordion showIcons="true" [slim]="true" [forActionSheet]="true">
                <go-accordion-panel title="Home Stuff" icon="home" [borderless]="true">
                  Stuff about home
                </go-accordion-panel>
              </go-accordion>
            </ng-container>
          </go-action-sheet>
        </div>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="placement">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Placement</h2>
      <go-copy cardId="placement" goCopyCardLink></go-copy>
    </ng-container>
    <div go-card-content>
      <div class="go-container">
        <div class="go-column--100 go-column go-column--no-padding">
          <h4 class="go-heading-4 go-heading--underlined">Code</h4>
          <p class="go-body-copy">
            A simple example to demo different placements defined by <code class="code-block--inline">placement</code>.
            <code [highlight]="placementGenericHtml"></code>
          </p>
        </div>
      </div>

      <div class="go-container">
        <div class="go-column go-column--100 go-column--no-padding">
          <h4 class="go-heading-4 go-heading--underlined">View</h4>
        </div>
        <div class="go-column go-column--20">
          <go-action-sheet placement="bottom">
            <ng-container go-action-sheet__button>
              <go-button>
                Bottom
              </go-button>
            </ng-container>
            <ng-container go-action-sheet-content>
              <go-panel panelContent="Panel 1">
              </go-panel>
              <go-panel panelContent="Panel 2">
              </go-panel>
              <go-panel panelContent="Panel 3">
              </go-panel>
            </ng-container>
          </go-action-sheet>
        </div>

        <div class="go-column go-column--20">
          <go-action-sheet placement="bottom" [shiftLeft]="true">
            <ng-container go-action-sheet__button>
              <go-button>
                Shift Left
              </go-button>
            </ng-container>
            <ng-container go-action-sheet-content>
              <go-panel panelContent="Panel 1">
              </go-panel>
              <go-panel panelContent="Panel 2">
              </go-panel>
              <go-panel panelContent="Panel 3">
              </go-panel>
            </ng-container>
          </go-action-sheet>
        </div>

        <div class="go-column go-column--20">
          <go-action-sheet placement="right">
            <ng-container go-action-sheet__button>
              <go-button>
                Right
              </go-button>
            </ng-container>
            <ng-container go-action-sheet-content>
              <go-panel panelContent="Panel 1">
              </go-panel>
              <go-panel panelContent="Panel 2">
              </go-panel>
              <go-panel panelContent="Panel 3">
              </go-panel>
            </ng-container>
          </go-action-sheet>
        </div>
      </div>
    </div>
  </go-card>
</section>
